import {
  FloatingPosition,
  Menu as MantineMenu,
  MenuDividerProps as MantineMenuDividerProps,
  MenuDropdownProps as MantineMenuDropdownProps,
  MenuItemProps as MantineMenuItemProps,
  MenuLabelProps as MantineMenuLabelProps,
  MenuProps as MantineMenuProps,
  MenuSubDropdownProps as MantineMenuSubDropdownProps,
  MenuSubItemProps as MantineMenuSubItemProps,
  MenuSubProps as MantineMenuSubProps,
  PolymorphicComponentProps,
  createPolymorphicComponent,
} from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { MENU_ZINDEX } from '@zorro/types';
import {
  ElementType,
  KeyboardEvent,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react';

import styles from './Menu.module.css';

/** Panel width axis. Small 240px / Medium 320px / Large 400px. */
export type MenuSize = 'sm' | 'md' | 'lg';

const ICON_SIZE = 16;
const ICON_STROKE = 1.25;

/**
 * Panel widths for the Figma `Size` axis.
 *
 * These MUST be driven through Popover's own `width` prop, not CSS: Popover
 * defaults `width: 'max-content'` and writes it as an INLINE style on the
 * dropdown, which beats any class-based rule. A `.dropdown[data-menu-size]`
 * width declaration is dead code.
 */
const MENU_WIDTHS: Record<MenuSize, number> = { sm: 240, md: 320, lg: 400 };

/**
 * Figma's 8px cascade gap is measured panel-edge to panel-edge, but Popover
 * measures `offset` from the trigger ROW, which the panel's own 8px padding
 * has already inset. 8 + 8 = 16.
 */
const SUBMENU_OFFSET = 16;

/** 4px gap between the trigger and the dropdown. */
const DROPDOWN_OFFSET = 4;

/**
 * `size` cannot live on the Mantine root: `Menu` renders no DOM, and a `size`
 * prop would leak to `Box` as the reserved `data-size`. It is carried in
 * context and emitted on the dropdown as `data-menu-size` instead.
 */
const MenuSizeContext = createContext<MenuSize>('md');

/**
 * One `classNames` object covers every compound part: Mantine's `Menu` resolves
 * it once on the root and hands it to all children via context.
 */
const menuClassNames = {
  dropdown: styles.dropdown,
  item: styles.item,
  itemLabel: styles.itemLabel,
  itemSection: styles.itemSection,
  itemIndicator: styles.itemIndicator,
  label: styles.label,
  divider: styles.divider,
  chevron: styles.chevron,
  arrow: styles.arrow,
  overlay: styles.overlay,
} as const;

/**
 * Slot styling is owned by the design system, and `variant` is not a Figma
 * axis, so all four are hidden from every part's public prop type.
 */
type OmittedProps = 'variant' | 'classNames' | 'styles' | 'vars';

/** Items additionally hide `color` — Figma has no coloured/destructive row. */
type OmittedItemProps = OmittedProps | 'color';

export interface MenuProps extends Omit<MantineMenuProps, OmittedProps> {
  /** Dropdown width: `sm` 240px, `md` 320px, `lg` 400px. Width only — row height and typography never scale. @default 'md' */
  size?: MenuSize;
}

/**
 * V2 Menu — dropdown menu panel.
 *
 * ```tsx
 * <Menu size="md">
 *   <Menu.Target>
 *     <Button>Actions</Button>
 *   </Menu.Target>
 *   <Menu.Dropdown>
 *     <Menu.Label>Group</Menu.Label>
 *     <Menu.Item leftSection={<IconPencil size={16} stroke={1.25} />}>Edit</Menu.Item>
 *     <Menu.Divider />
 *     <Menu.Item disabled>Delete</Menu.Item>
 *   </Menu.Dropdown>
 * </Menu>
 * ```
 */
export function Menu({
  size = 'md',
  position = 'bottom-end',
  offset = DROPDOWN_OFFSET,
  withArrow = false,
  zIndex = MENU_ZINDEX,
  children,
  ...props
}: MenuProps) {
  return (
    <MenuSizeContext.Provider value={size}>
      <MantineMenu
        unstyled
        position={position}
        offset={offset}
        withArrow={withArrow}
        zIndex={zIndex}
        width={MENU_WIDTHS[size]}
        classNames={menuClassNames}
        {...props}
      >
        {children}
      </MantineMenu>
    </MenuSizeContext.Provider>
  );
}

/* ===== Dropdown ===== */

export interface MenuDropdownProps extends Omit<
  MantineMenuDropdownProps,
  OmittedProps
> {
  /**
   * Caps the scrollable row region (Figma's `Items` layer). Uncapped by
   * default — Figma's own sources conflict (570 / 240 / "15 rows").
   *
   * Set this rather than putting `max-height` on the dropdown yourself:
   * Mantine hardcodes `withinPortal: false` on `Menu.Sub`, so sub-panels are
   * DOM descendants of the dropdown. Making the DROPDOWN the scroll container
   * makes it both the clip box and the offset parent, and submenus get
   * clipped and mis-positioned on top of the parent's own rows. The inner
   * region is `position: static`, so an absolutely-positioned sub-panel is
   * laid out against the dropdown and escapes the scroll clip.
   */
  maxHeight?: number | string;
  /**
   * Figma's `Action` boolean — a pinned footer (divider + row) that sits
   * OUTSIDE the scroll region so it stays visible while the rows scroll.
   */
  footer?: ReactNode;
}

const MenuDropdown = forwardRef<HTMLDivElement, MenuDropdownProps>(
  function MenuDropdown({ children, maxHeight, footer, ...props }, ref) {
    const size = useContext(MenuSizeContext);

    return (
      <MantineMenu.Dropdown {...props} ref={ref} mod={{ 'menu-size': size }}>
        <div
          className={styles.items}
          style={
            maxHeight === undefined
              ? undefined
              : { maxHeight, overflowY: 'auto' }
          }
        >
          {children}
        </div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </MantineMenu.Dropdown>
    );
  }
);

/* ===== Item ===== */

interface MenuItemContentProps {
  /** Secondary line under the label. Wraps (the label truncates). */
  description?: ReactNode;
  /** Selected / "current value" background. Also used for the row whose submenu is open. */
  isSelected?: boolean;
  /** Figma `Content=Sub-item` — inline indented child row (24px left padding). One level only. */
  isSubItem?: boolean;
}

export interface MenuItemProps
  extends Omit<MantineMenuItemProps, OmittedItemProps>, MenuItemContentProps {
  /** Show the submenu chevron without using `Menu.Sub` (e.g. an inline expandable group). */
  withSubmenuChevron?: boolean;
}

/** Shared label/description body so `Menu.Item` and `Menu.Sub.Item` render identically. */
function MenuItemBody({
  children,
  description,
}: {
  children?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <>
      <span className={styles.itemTitle}>{children}</span>
      {description ? (
        <span className={styles.itemDescription}>{description}</span>
      ) : null}
    </>
  );
}

const SubmenuChevron = () => (
  <IconChevronRight size={ICON_SIZE} stroke={ICON_STROKE} />
);

const _MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  function MenuItem(
    {
      children,
      description,
      isSelected,
      isSubItem,
      withSubmenuChevron,
      rightSection,
      ...props
    },
    ref
  ) {
    return (
      // `className` is intentionally left in `...props`: Mantine's `getStyles`
      // merges it with `classNames.item` rather than replacing it.
      // `...props` comes first so a consumer cannot clobber the derived `mod`.
      <MantineMenu.Item
        {...props}
        ref={ref}
        mod={{
          selected: isSelected || undefined,
          'sub-item': isSubItem || undefined,
        }}
        rightSection={
          rightSection ?? (withSubmenuChevron ? <SubmenuChevron /> : undefined)
        }
      >
        <MenuItemBody description={description}>{children}</MenuItemBody>
      </MantineMenu.Item>
    );
  }
);

_MenuItem.displayName = 'Menu.Item';

/**
 * Polymorphic so a row can render as an anchor / Next.js `Link`
 * (`<Menu.Item component={Link} href="…">`). Mantine's own polymorphic factory
 * type rejects a widened `ElementType`, so the wrapper is re-wrapped here the
 * same way `v2/Button` does it.
 */
type MenuItemComponent = <C extends ElementType = 'button'>(
  props: PolymorphicComponentProps<C, MenuItemProps>
) => React.ReactElement | null;

const MenuItem = createPolymorphicComponent<'button', MenuItemProps>(
  _MenuItem
) as unknown as MenuItemComponent;

/* ===== Label (Figma: Title) ===== */

export type MenuLabelProps = Omit<MantineMenuLabelProps, OmittedProps>;

const MenuLabel = forwardRef<HTMLDivElement, MenuLabelProps>(function MenuLabel(
  { children, ...props },
  ref
) {
  return (
    <MantineMenu.Label ref={ref} {...props}>
      {children}
    </MantineMenu.Label>
  );
});

/* ===== Divider ===== */

export type MenuDividerProps = Omit<MantineMenuDividerProps, OmittedProps>;

const MenuDivider = forwardRef<HTMLDivElement, MenuDividerProps>(
  function MenuDivider(props, ref) {
    return <MantineMenu.Divider ref={ref} {...props} />;
  }
);

/* ===== Sub (cascading flyout) ===== */

export type MenuSubProps = Omit<MantineMenuSubProps, OmittedProps>;

export type MenuSubDropdownProps = Omit<
  MantineMenuSubDropdownProps,
  OmittedProps
>;

export type MenuSubItemProps = Omit<MantineMenuSubItemProps, OmittedItemProps> &
  MenuItemContentProps;

/**
 * Restores ArrowLeft focus when stepping back out of a submenu.
 *
 * Upstream bug in @mantine/core 9.3.1: `MenuSub.focusParentItem()` looks the
 * trigger up as `` `${id}-target` ``, but `Popover` exposes the target as the
 * bare `uid` (`getTargetId: () => uid`) and only the DROPDOWN is suffixed
 * (`` `${uid}-dropdown` ``). The lookup never matches, so ArrowLeft closes the
 * level and drops focus to `<body>` — and the next ArrowLeft then exits the
 * whole menu instead of stepping back one more level.
 *
 * The dropdown's own id gives us the uid, so we can refocus the trigger
 * ourselves. Mantine still performs the close; this only restores focus.
 * Remove once the upstream lookup is fixed.
 */
function handleSubDropdownArrowLeft(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'ArrowLeft') {
    return;
  }

  // Sub-dropdowns nest, so ignore events bubbling up from a deeper level —
  // that level refocuses its own trigger.
  const origin = (event.target as HTMLElement | null)?.closest(
    '[data-menu-dropdown]'
  );

  if (origin !== event.currentTarget) {
    return;
  }

  const uid = event.currentTarget.id.replace(/-dropdown$/u, '');

  if (!uid) {
    return;
  }

  // Mantine's own focus attempt runs on a 16ms timeout; queue after it so we
  // are not immediately overwritten.
  window.setTimeout(() => document.getElementById(uid)?.focus(), 32);
}

const MenuSubDropdown = forwardRef<HTMLDivElement, MenuSubDropdownProps>(
  function MenuSubDropdown({ children, onKeyDown, ...props }, ref) {
    const size = useContext(MenuSizeContext);

    return (
      <MantineMenu.Sub.Dropdown
        {...props}
        ref={ref}
        mod={{ 'menu-size': size }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          handleSubDropdownArrowLeft(event);
        }}
      >
        <div className={styles.items}>{children}</div>
      </MantineMenu.Sub.Dropdown>
    );
  }
);

const _MenuSubItem = forwardRef<HTMLButtonElement, MenuSubItemProps>(
  function MenuSubItem(
    { children, description, isSelected, isSubItem, rightSection, ...props },
    ref
  ) {
    return (
      <MantineMenu.Sub.Item
        {...props}
        ref={ref}
        mod={{
          selected: isSelected || undefined,
          'sub-item': isSubItem || undefined,
        }}
        rightSection={rightSection ?? <SubmenuChevron />}
      >
        <MenuItemBody description={description}>{children}</MenuItemBody>
      </MantineMenu.Sub.Item>
    );
  }
);

_MenuSubItem.displayName = 'Menu.Sub.Item';

type MenuSubItemComponent = <C extends ElementType = 'button'>(
  props: PolymorphicComponentProps<C, MenuSubItemProps>
) => React.ReactElement | null;

const MenuSubItem = createPolymorphicComponent<'button', MenuSubItemProps>(
  _MenuSubItem
) as unknown as MenuSubItemComponent;

/**
 * Cascading submenu. Opens to the right of the triggering row with an 8px gap,
 * top-aligned to that row (Figma `Menu with submenus`). Hover intent /
 * safe-area handling is Mantine's default.
 */
function MenuSub({
  position = 'right-start',
  offset = SUBMENU_OFFSET,
  zIndex = MENU_ZINDEX,
  onPositionChange,
  children,
  ...props
}: MenuSubProps) {
  // Sub-panels are separate Popovers, so they need the size and z-index
  // applied again — they inherit neither from the root Menu.
  const size = useContext(MenuSizeContext);
  // Mantine draws the hover safe-area from `position`, not from where Popover
  // flipped the panel, so a flipped submenu closes as the cursor heads to it.
  const [flippedPosition, setFlippedPosition] = useState<FloatingPosition>();

  return (
    <MantineMenu.Sub
      position={flippedPosition ?? position}
      onPositionChange={(renderedPosition) => {
        setFlippedPosition(renderedPosition);
        onPositionChange?.(renderedPosition);
      }}
      offset={offset}
      zIndex={zIndex}
      width={MENU_WIDTHS[size]}
      {...props}
    >
      {children}
    </MantineMenu.Sub>
  );
}

MenuSub.Target = MantineMenu.Sub.Target;
MenuSub.Dropdown = MenuSubDropdown;
MenuSub.Item = MenuSubItem;

Menu.Target = MantineMenu.Target;
Menu.Dropdown = MenuDropdown;
Menu.Item = MenuItem;
Menu.Label = MenuLabel;
Menu.Divider = MenuDivider;
Menu.Sub = MenuSub;
